/**
 * Request Deduplication + SWR Cache
 *
 * - Concurrent identical requests share one Promise (dedup)
 * - Stale data returned immediately + background refresh (SWR)
 * - Session-scoped: cache clears on page reload, no timer-based GC
 */

import { registerInvalidateCallback } from "../store/feed-store"

interface CacheEntry<T> {
  data: T
  timestamp: number
}

interface FetchOptions<T> {
  staleTime: number
  force?: boolean
  onStaleRevalidate?: (fresh: T) => void
}

interface FetchResult<T> {
  data: T | undefined
  source: "cache" | "network"
}

/**
 * Type-erased cache — callers MUST use globally unique keys per T type.
 * Key collisions between different T types will cause silent runtime errors.
 */
const cache = new Map<string, CacheEntry<unknown>>()
const inflight = new Map<string, Promise<unknown>>()

export async function dedupFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: FetchOptions<T>,
): Promise<FetchResult<T>> {
  const entry = cache.get(key) as CacheEntry<T> | undefined
  const now = Date.now()

  // 1. Fresh → return cached
  if (!options.force && entry && now - entry.timestamp < options.staleTime) {
    return { data: entry.data, source: "cache" }
  }

  // 2. Stale (has cache but past staleTime) → return cache + background refresh
  if (!options.force && entry) {
    if (!inflight.has(key)) {
      const bgPromise = fetcher()
        .then((fresh) => {
          cache.set(key, { data: fresh, timestamp: Date.now() })
          options.onStaleRevalidate?.(fresh)
          return fresh
        })
        .catch(() => {
          // SWR background refresh failed — stale data already returned to caller
        })
        .finally(() => inflight.delete(key))
      inflight.set(key, bgPromise)
    }
    return { data: entry.data, source: "cache" }
  }

  // 3. No cache or force → fetch (deduplicated)
  const existing = !options.force ? (inflight.get(key) as Promise<T> | undefined) : undefined
  if (existing) {
    const data = await existing
    return { data, source: "network" }
  }

  const promise = fetcher()
    .then((data) => {
      cache.set(key, { data, timestamp: Date.now() })
      return data
    })
    .finally(() => inflight.delete(key))
  inflight.set(key, promise)

  const data = await promise
  return { data, source: "network" }
}

/** Invalidate a single cache entry by exact key */
export function invalidateCache(key: string): void {
  cache.delete(key)
}

/** Invalidate all cache entries whose key starts with the given prefix */
export function invalidateCachePrefix(prefix: string): void {
  for (const k of cache.keys()) {
    if (k.startsWith(prefix)) cache.delete(k)
  }
}

// Register the callback so feedStore.invalidate() can clear dedup cache
registerInvalidateCallback(invalidateCachePrefix)
